import * as React from 'react';
import type { WithRequired } from '../../../../modules/ts.utils';
import type { ReactRef } from '../../../../modules/react-utils';
import { assignRefs } from '../../../../modules/react-utils';
import cn from '~modules/cn';

import './list-item.scss';

export type ListItemSize = 'medium' | 'large' | 'x-large' | 'full' | 'medium-full';

export type ListItemProps = {
    className?: string;
    dataTip?: string;
    dataTipOverflow?: boolean;
    dataTipDelay?: number;
    dataTipPlacement?: string;
    dataTestId?: string;
    innerTestId?: string;
    text?: React.ReactNode;
    bottomText?: React.ReactNode;
    value?: string;
    selected?: boolean;
    highlighted?: boolean;
    disabled?: boolean;
    size?: ListItemSize;
    onClick?: (event: React.MouseEvent) => void;
    onMouseDown?: (event: React.MouseEvent | React.TouchEvent) => void;
    leftView?: React.ReactNode;
    rightView?: React.ReactNode;
    children?: React.ReactNode;
    style?: React.CSSProperties;
    Tag?: 'li' | 'div';
    Control?: React.ComponentType | 'div' | 'button';
    tabIndex?: number;
    static?: boolean;
    setRef?: ReactRef<HTMLElement | null>;
    level?: string | number;
    type?: 'primary' | 'secondary' | 'tertiary';
    toggleButton?: boolean;
    controlProps?: Record<string, unknown>;
};

const defaultProps: WithRequired<ListItemProps, 'size' | 'Tag' | 'Control'> = {
    size: 'large',
    Tag: 'li',
    Control: 'div'
};

const ListItem = (props: ListItemProps): JSX.Element => {
    const {
        children,
        tabIndex,
        Tag,
        Control,
        className,
        dataTip,
        dataTipOverflow,
        dataTipDelay,
        dataTipPlacement,
        dataTestId,
        innerTestId,
        text,
        bottomText,
        value,
        selected,
        highlighted,
        disabled,
        onClick,
        onMouseDown,
        size,
        leftView,
        rightView,
        style,
        setRef,
        type = 'primary',
        toggleButton,
        controlProps
    } = {
        ...defaultProps,
        ...props
    };
    const mainClass = cn('list-item', `list-item--${type}`, `list-item--${size}`, className, {
        'list-item--static': props.static || (children && !onClick),
        'list-item--selected': selected,
        'list-item--highlighted': highlighted,
        'list-item--disabled': disabled,
        'list-item--has-children': children,
        'list-item--two-rows': text && bottomText
    });

    return (
        <Tag style={style} data-testid={dataTestId} ref={assignRefs(setRef)}>
            <Control
                value={value}
                onClick={disabled ? undefined : onClick}
                onMouseDown={disabled ? undefined : onMouseDown}
                onTouchStart={disabled ? undefined : onMouseDown}
                className={mainClass}
                data-tip={dataTip}
                data-tip-overflow={dataTipOverflow ? 'list-item__text' : undefined}
                data-tip-delay={dataTipDelay}
                data-tip-placement={dataTipPlacement}
                tabIndex={tabIndex}
                data-testid="list-item-container"
                style={{ '--list-item-level': props.level } as React.CSSProperties}
                aria-pressed={toggleButton ? selected : undefined}
                {...controlProps}
            >
                <span data-testid={innerTestId} className="list-item__value">
                    {leftView}
                    {text && (
                        <span className="list-item__text-container">
                            <span className="list-item__text">{text}</span>
                            {bottomText && <span className="list-item__bottom-text">{bottomText}</span>}
                        </span>
                    )}
                </span>
                {rightView}
            </Control>
            {children && <div className={cn({ 'list-item-children': !props.level })}>{children}</div>}
        </Tag>
    );
};

export default ListItem;
